// B2a — workspace home: the landing page for signed-in roles that have no module home (e.g. EMPLOYEE).
// Lists only destinations this role can open; phase workspaces appear only when the phase is enabled for the tenant.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { AdminAppShell } from "../design-system/shells/AdminAppShell";
import { useAuth } from "../lib/auth";
import { usePhaseFeature, ARCHITECT_ROLES, FIELD_ROLES, FINANCE_ROLES } from "../lib/phaseFeatures";
import { PROPERTY_ROLES } from "./phase7/Property";

function Destination({ to, title, sub }) {
  return (
    <Link to={to} data-testid={`workspace-home-link-${to.replace(/\//g, "-")}`}
          className="bg-white border border-erf-borderCream rounded-md p-4 min-h-[44px] flex items-start justify-between gap-3 hover:border-erf-gold/60 hover:shadow-md transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
      <div>
        <div className="font-display font-semibold text-erf-dark">{title}</div>
        <div className="text-xs font-nav text-erf-dark/60 mt-0.5">{sub}</div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-erf-dark/30" aria-hidden="true" />
    </Link>
  );
}

export default function WorkspaceHome() {
  const { user } = useAuth();
  const role = user?.role_key;
  const p3 = usePhaseFeature(3, user?.id);
  const p4 = usePhaseFeature(4, user?.id);
  const p5 = usePhaseFeature(5, user?.id);
  const p7 = usePhaseFeature(7, user?.id);
  const items = [
    p3?.enabled && ARCHITECT_ROLES.includes(role) && { to: "/phase3/architect", title: "Architect Workspace", sub: "Drawings, revisions and design review" },
    p4?.enabled && FIELD_ROLES.includes(role) && { to: "/phase4/field", title: "Field Operations", sub: "Daily logs and site records" },
    p5?.enabled && FINANCE_ROLES.includes(role) && { to: "/phase5/billing", title: "Financials", sub: "Billing and pay applications" },
    p7?.enabled && PROPERTY_ROLES.includes(role) && { to: "/phase7/properties", title: "Property Management", sub: "Properties, units and work orders" },
    { to: "/learn", title: "Help & Learn", sub: "Guides and tutorials" },
    { to: "/help", title: "Help Center", sub: "Answers and support" },
  ].filter(Boolean);
  return (
    <AdminAppShell activeNav="command" title="Home" subtitle={user?.display_name || user?.email || ""} user={user}>
      <div className="mx-auto w-full max-w-[1200px] box-border px-4 md:px-6 lg:px-8" data-testid="workspace-home">
        <h2 className="font-display font-semibold text-lg text-erf-dark mb-1">Your workspace</h2>
        <p className="text-sm font-nav text-erf-dark/70 mb-4" data-testid="workspace-home-role">
          Signed in as {(role || "").replaceAll("_", " ").toLowerCase()}. These are the areas your role can open.
        </p>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => <Destination key={it.to} {...it} />)}
        </div>
      </div>
    </AdminAppShell>
  );
}
